import React from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useNavigate } from 'react-router-dom';
    import { useNotifications } from '@/hooks/useNotifications';
    import { useAuth } from '@/contexts/AuthContext';
    import { formatDate } from '@/lib/utils';
    import { Button } from '@/components/ui/Button';
    import { FiBell, FiCheck, FiInfo, FiAlertTriangle, FiCheckCircle, FiArrowRight } from 'react-icons/fi';

    // Map notification types/titles to relevant dashboard routes based on user role
    function resolveNotificationLink(notification, role) {
      if (notification.link) return notification.link;
      
      const title = (notification.title || '').toLowerCase();
      const type = (notification.type || '').toLowerCase();

      // Business Routes
      if (role === 'business') {
        if (title.includes('artwork revision') || title.includes('action required')) return '/dashboard/business/campaigns?tab=revisions';
        return '/dashboard/business/campaigns';
      }

      // Driver Routes
      if (role === 'driver') {
        if (title.includes('assigned') || title.includes('campaign assigned') || title.includes('verification status')) return '/dashboard/driver';
        if (title.includes('application update') || title.includes('status update')) return '/dashboard/driver/tasks';
        return '/dashboard/driver'; // Fallback for drivers
      }

      // Admin Routes
      if (role === 'admin') {
        // FIX: Explicitly check for print shop issues FIRST so it routes to campaigns
        if (title.includes('print shop issue') || title.includes('artwork')) return '/dashboard/admin/campaigns';
        
        if (title.includes('vehicle') || title.includes('verification') || title.includes('documents') || type === 'verification') return '/dashboard/admin/drivers';
        if (title.includes('application') || type === 'application') return '/dashboard/admin/approvals';
        if (title.includes('campaign') || type === 'campaign_update' || type === 'success') return '/dashboard/admin/campaigns';
        if (title.includes('pasting')) return '/dashboard/admin/campaigns';
        if (title.includes('payout') || title.includes('payment')) return '/dashboard/admin/payouts';
        if (title.includes('print') || title.includes('shop')) return '/dashboard/admin/printing-shops';
        
        return '/dashboard/admin'; // Safe Fallback for admins
      }

      return '/dashboard'; // Global Fallback
    }

    const NotificationIcon = ({ type }) => {
      switch (type) {
        case 'success': return <FiCheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />;
        case 'warning': return <FiAlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />;
        case 'error': return <FiAlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />;
        default: return <FiInfo className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />;
      }
    };

    export function NotificationsPage() {
      const { notifications, loading, markAsRead, markAllAsRead } = useNotifications();
      const navigate = useNavigate();
      const { profile } = useAuth();
      
      const hasUnread = notifications.some(n => !n.is_read);

      const handleView = async (notification) => {
        const link = resolveNotificationLink(notification, profile?.role);
        if (!notification.is_read) await markAsRead(notification.id);
        if (link) navigate(link);
      };

      if (loading) {
        return (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse mb-6" />
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl" />
            ))}
          </div>
        );
      }

      return (
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Notifications</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {notifications.length} total · {notifications.filter(n => !n.is_read).length} unread
              </p>
            </div>
            {hasUnread && (
              <Button onClick={markAllAsRead} variant="outline" size="sm">
                <FiCheck className="w-3.5 h-3.5 mr-1.5" /> Mark all read
              </Button>
            )}
          </div>

          <AnimatePresence mode="popLayout">
            {notifications.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiBell className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">All caught up!</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">No notifications to show.</p>
              </motion.div>
            ) : (
              notifications.map((notification, i) => {
                const link = resolveNotificationLink(notification, profile?.role);
                return (
                  <motion.div 
                    key={notification.id} 
                    layout
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ delay: i * 0.04 }}
                    className={`p-5 rounded-2xl border transition-all ${notification.is_read ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800' : 'bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800/50'}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex gap-3 flex-1">
                        <NotificationIcon type={notification.type} />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                            {notification.title}
                          </h3>
                          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm leading-relaxed">
                            {notification.message}
                          </p>
                          <span className="text-xs text-slate-400 dark:text-slate-500 mt-2 block">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {link && (
                          <button 
                            onClick={() => handleView(notification)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors shadow-sm"
                          >
                            View <FiArrowRight className="w-3 h-3" />
                          </button>
                        )}
                        {!notification.is_read && (
                          <button 
                            onClick={() => markAsRead(notification.id)}
                            className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-xl transition-colors"
                            title="Mark as read"
                          >
                            <FiCheck className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      );
    }